import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useSidebarFilter } from '@/components/Layout/AppSidebar/useSidebarFilter';
import type { SidebarNavItem } from '@/components/Layout/AppSidebar/useSidebarNavigation';
import { useSidebarNavigation } from '@/components/Layout/AppSidebar/useSidebarNavigation';
import { useAuth } from '@/contexts/auth';
import { useDemoMode } from '@/contexts/demo/DemoContext';
import { useGetClientOverview } from '@/domain/client';
import { useRouteInfo } from '@/hooks/useRouteInfo';
import { applyDemoMaskToClient, applyDemoMaskToOverviewLocation } from '@/utils/demoMasking';

import {
    buildCageQuickSearchItems,
    buildLocationQuickSearchItems,
    locationQuickSearchLabel,
} from './quickSearchHelpers';
import type { QuickSearchItem } from './types';

export function useQuickSearchItems(isOpen: boolean): QuickSearchItem[] {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { clients, canAdministerLocation, isOptoScaleAdmin } = useAuth();
    const { isDemoMode } = useDemoMode();
    const { clientId } = useRouteInfo();

    const internalPage = pathname.startsWith('/internal');

    // 1. Fetch active client's overview (for cages)
    const { data: clientOverview } = useGetClientOverview(clientId ?? 0, {
        enabled: isOpen && clientId !== undefined && clientId > 0,
    });

    // 2. Get sidebar items (pages) and visibility filter
    const { items, staticItems, level, params } = useSidebarNavigation();
    const filterItems = useSidebarFilter(level, params);

    // Filter sidebar navigation items based on current context visibility
    const visibleItems = useMemo(() => {
        if (!isOpen || !level) return [];
        return filterItems([...items, ...staticItems]);
    }, [isOpen, level, filterItems, items, staticItems]);

    // Flatten pages recursively
    const pages = useMemo<QuickSearchItem[]>(() => {
        if (!isOpen) return [];

        const result: QuickSearchItem[] = [];
        const seen = new Set<string>();

        const recurse = (item: SidebarNavItem, parentLabel?: string) => {
            if (!item.fullUrl || !item.label) return;
            const translatedLabel = t(item.label);
            const key = `${translatedLabel}-${item.fullUrl}`;

            if (!seen.has(key)) {
                seen.add(key);
                result.push({
                    id: `page-${item.id}`,
                    kind: 'page',
                    label: translatedLabel,
                    group: 'Pages',
                    url: item.fullUrl,
                    icon: item.icon,
                    sourceIndex: result.length,
                    ...(parentLabel ? { parentLabel } : {}),
                });
            }

            if (item.children) {
                item.children.forEach((child) => {
                    recurse(child, translatedLabel);
                });
            }
        };

        visibleItems.forEach((item) => {
            recurse(item);
        });
        return result;
    }, [isOpen, visibleItems, t]);

    // 3. Process clients and locations
    const displayClients = useMemo(() => {
        if (!isOpen) return [];
        if (!isDemoMode || internalPage) {
            return clients;
        }
        return clients.map((client) => applyDemoMaskToClient(client, t));
    }, [isOpen, clients, isDemoMode, internalPage, t]);

    const clientItems = useMemo<QuickSearchItem[]>(() => {
        if (!isOpen) return [];
        // Hide the Clients section entirely for non-OptoScale admins
        if (!isOptoScaleAdmin) return [];

        return displayClients.map((client) => ({
            id: `client-${client.id}`,
            kind: 'client',
            label: client.name,
            group: 'Clients',
            url: `/c/${client.id}`,
        }));
    }, [isOpen, displayClients, isOptoScaleAdmin]);

    const displayOverview = useMemo(() => {
        if (!isOpen || !clientOverview) {
            return { activeLocations: [], inactiveLocations: [] };
        }
        if (!isDemoMode || internalPage) {
            return {
                activeLocations: clientOverview.activeLocations,
                inactiveLocations: clientOverview.inactiveLocations,
            };
        }
        return {
            activeLocations: clientOverview.activeLocations.map((loc) =>
                applyDemoMaskToOverviewLocation(loc, t)
            ),
            inactiveLocations: clientOverview.inactiveLocations.map((loc) =>
                applyDemoMaskToOverviewLocation(loc, t)
            ),
        };
    }, [isOpen, clientOverview, isDemoMode, internalPage, t]);

    const locationItems = useMemo<QuickSearchItem[]>(() => {
        if (!isOpen) return [];

        const result: QuickSearchItem[] = [];
        const overviewLocationIds = new Set<number>();
        const currentClient = displayClients.find((client) => client.id === clientId);

        if (clientId && currentClient) {
            const overviewItems = buildLocationQuickSearchItems({
                activeLocations: displayOverview.activeLocations,
                inactiveLocations: displayOverview.inactiveLocations,
                clientId,
                clientName: currentClient.name,
                isOptoScaleAdmin,
                canAdminister: (locationId) => canAdministerLocation(clientId, locationId),
            });
            for (const item of overviewItems) {
                result.push(item);
            }
            for (const location of [
                ...displayOverview.activeLocations,
                ...displayOverview.inactiveLocations,
            ]) {
                overviewLocationIds.add(location.locationId);
            }
        }

        for (const client of displayClients) {
            for (const loc of client.locations) {
                if (client.id === clientId && overviewLocationIds.has(loc.id)) {
                    continue;
                }
                if (canAdministerLocation(client.id, loc.id)) {
                    result.push({
                        id: `location-${loc.id}`,
                        kind: 'location',
                        label: locationQuickSearchLabel(loc.name, client.name, isOptoScaleAdmin),
                        group: 'Locations',
                        url: `/c/${client.id}/location/${loc.id}`,
                    });
                }
            }
        }
        return result;
    }, [
        isOpen,
        displayClients,
        clientId,
        displayOverview,
        canAdministerLocation,
        isOptoScaleAdmin,
    ]);

    const displayLocations = useMemo(
        () => [...displayOverview.activeLocations, ...displayOverview.inactiveLocations],
        [displayOverview]
    );

    const cageItems = useMemo<QuickSearchItem[]>(() => {
        if (!isOpen || !clientId) return [];

        const result: QuickSearchItem[] = [];
        for (const location of displayLocations) {
            // Filter out cages under locations that the user does not have permission to access
            if (canAdministerLocation(clientId, location.locationId)) {
                result.push(...buildCageQuickSearchItems(location, clientId));
            }
        }

        return result;
    }, [isOpen, displayLocations, clientId, canAdministerLocation]);

    // Hierarchy Order: Pages -> Clients -> Locations -> Cages
    return useMemo<QuickSearchItem[]>(() => {
        if (!isOpen) return [];
        return [...pages, ...clientItems, ...locationItems, ...cageItems];
    }, [isOpen, pages, clientItems, locationItems, cageItems]);
}
